import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from "react-native";
import { ReactCountryFlag } from "react-country-flag";
import { sendMoneyService } from "../../../../services/sendmoney.service";
import { useApiWithLoading } from "../../../../hooks/useApiWithLoading";
import Colors from "../../../../constants/colors";
import RadioSelectedIcon from "../../../../../../../assets/images/RadioSelectButton.svg";
import FlatListDataComponent from "../../common/FlatListDataComponent";
import type { Country } from "../models/country";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { sendWorkflowManager } from "../sendWorkflow/builder/workflowManager";

export function CountryScreen() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [countriesData, setCountriesData] = useState<Country[]>([]);
  const updateJourneyData = useJourneyStore((state) => state.updateJourneyData);
  const { goNext } = useWorkflowNavigation();
  const { executeWithLoading } = useApiWithLoading();

  const countryCodeToEmoji = (code: string) => {
    if (!code) return "";
    const upper = code.toUpperCase();
    if (upper.length !== 2) return "";
    const base = 127397;
    return [...upper]
      .map((c) => String.fromCodePoint(base + c.charCodeAt(0)))
      .join("");
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const result = await executeWithLoading(
          () => sendMoneyService.loadCountries(),
          "Processing..."
        );
        const active = result.countries.filter(
          (c) => c.receiveActive && !!c.name && !!c.iso3Code
        );
        setCountriesData(active);
      } catch (e) {
        console.error("Failed to load countries in CountryScreen", e);
      }
    };
    fetchCountries();
  }, []);

  const filteredCountries = useMemo(() => {
    const q = search.toLowerCase();
    return countriesData.filter((c) =>
      (c.name || "").toLowerCase().includes(q)
    );
  }, [countriesData, search]);

  const onSelectCountry = (item: Country) => {
    setSelectedCountry(item.name);
    // if selected country has subdivisions, then update sendWorkflowManager countryHasStates to true. 
    const countryHasStates = item.subdivisions.length > 0;
    sendWorkflowManager.updateContext({ countryHasStates: countryHasStates });
    updateJourneyData({ destinationCountry: item });
    setTimeout(() => {
      goNext();
    }, 200);
  };

  return (
    <View style={styles.container} testID="country-screen">
      <FlatListDataComponent
        title={t("COUNTRY_STEP_TITLE")}
        subTitle={t("COUNTRY_STEP_SUBTITLE")}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData={filteredCountries}
        searchTestID="sendMoneyCountrySearchInput"
        flatListTestID="sendMoneyCountryList"
        renderItem={(item: Country) => {
          const alpha2 = item.iso2Code?.toUpperCase() || "";
          return (
            <TouchableOpacity
              style={styles.countryRow}
              onPress={() => onSelectCountry(item)}
              testID="sendMoneyCountrySelected"
            >
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                {Platform.OS === "web" ? (
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 50,
                      overflow: "hidden",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 10,
                    }}
                  >
                    {alpha2 ? (
                      <ReactCountryFlag
                        countryCode={alpha2}
                        svg
                        style={{
                          width: 35,
                          height: 35,
                          borderRadius: 50,
                        }}
                        aria-label={item.name}
                      />
                    ) : (
                      <Text style={{ fontSize: 20 }}>🌍</Text>
                    )}
                  </View>
                ) : (
                  <Text style={styles.flag}>
                    {countryCodeToEmoji(alpha2) || "🌍"}
                  </Text>
                )}
                <Text style={styles.countryName}>{item.name}</Text>
              </View>
              <View style={styles.radioButton}>
                {selectedCountry === item.name && (
                  <RadioSelectedIcon width={20} height={20} />
                )}
              </View>
            </TouchableOpacity>
          );
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 0,
    justifyContent: "space-between",
  },
  flag: {
    fontSize: 22,
    marginRight: 12,
  },
  countryName: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: "Rubik",
    lineHeight: 20,
    color: Colors.darkGrey,
    paddingLeft: 16,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.radioButtonFilled,
  },
});
